// Settings in the command palette (spec/13 §2, §13; spec/14 §14): typing a setting's name, its key or
// words from its description lists it, and choosing it opens its settings screen at the field.
//
// Nothing here knows a setting by name. The index is built from the settings screens' own views —
// `GET …/venues/{v}/settings`, `GET …/organizations/{o}/settings`, HQ's `GET /v1/hq/settings` — which
// the server has already filtered to what this member may see there (`visibleTo`, the plan, the
// screen's scope) and marked `editable` for what they may change, plus the venue record's fields on
// the Venue profile group (`venue-profile/fields.ts`). A setting the screen lists is a setting the
// palette can find; one it does not is not.
//
// Matching is word-prefix, every query word must match somewhere, and a few words are folded to one
// before matching (top up / recharge, pay as you go / PAYG, UPI ID / VPA, minimum / smallest …) so the
// words a venue uses find the words the registry uses. Pure, so the ranking is unit-tested.

import type { SettingEntry, SettingsView } from '@clavian/api-client';

import { placementOf } from './layout';
import type { SettingsScope } from './registry-source';

/** Where a target's settings screen lives: a route with a `$group` parameter. */
export interface SettingsTarget {
  /** Stable: `venue:<id>`, `organization:<id>`, `platform`. */
  readonly id: string;
  readonly scope: SettingsScope;
  /** What a result says about where it is: "Venue: Central", "Organisation", "Platform defaults". */
  readonly scopeLabel: string;
  /** The screen's route, whose `$group` parameter the result fills in. */
  readonly to: string;
  readonly params: Readonly<Record<string, string>>;
  /** The venue's screen also holds the venue record's fields, in the Venue profile group. */
  readonly venueProfile?: boolean;
}

/** One searchable setting at one scope. */
export interface SettingDoc {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly description: string;
  /** The job group it is drawn in (`layout.ts`): the screen's `$group`. */
  readonly group: string;
  readonly groupLabel: string;
  /** The row to reveal: its own key, or the panel that is its home (pay-as-you-go's summary, the tax registration). */
  readonly anchor: string;
  readonly target: SettingsTarget;
  /** The member cannot change it here — a lock above, their role, or a scope it is not set at. */
  readonly readOnly: boolean;
}

export interface IndexWords {
  readonly label: (key: string) => string;
  /** The console's one line of help for a key; the registry's description when there is none. */
  readonly help?: (key: string) => string | undefined;
  /** The words of a setting's choices, so "USB" finds "Read only" and "Block" finds the USB row. */
  readonly options?: (entry: SettingEntry) => string;
  readonly groupLabel: (group: string, view: SettingsView | undefined) => string;
  /** The venue record's fields: their label and description. */
  readonly venueField: (key: string) => { readonly label: string; readonly description: string };
}

export interface LoadedTarget {
  readonly target: SettingsTarget;
  /** The screen's view; absent while loading or when the member may not read it. */
  readonly view: SettingsView | undefined;
}

/** The venue record's fields are drawn in the Venue group. */
const VENUE_GROUP = 'venue';
/** Scopes a venue's screen can store a value at (spec/03 §9): the venue, and places inside it. */
const VENUE_LEVEL: readonly string[] = ['venue', 'stationClass', 'zone'];

/**
 * Every setting the loaded screens list, once per screen. A venue's screen also shows what is only
 * stored above it, read-only; when the organisation's screen is reachable too, that setting is
 * offered there instead of twice.
 */
export function buildSettingsIndex(loaded: readonly LoadedTarget[], venueFields: readonly string[], words: IndexWords): SettingDoc[] {
  const organizationKeys = new Set<string>();
  for (const { target, view } of loaded) {
    if (view === undefined || target.scope.kind !== 'organization') continue;
    for (const entry of view.settings) organizationKeys.add(entry.key);
  }
  const docs: SettingDoc[] = [];
  for (const { target, view } of loaded) {
    if (target.venueProfile === true) {
      for (const key of venueFields) {
        const field = words.venueField(key);
        docs.push({
          id: `${target.id}:${key}`,
          key,
          label: field.label,
          description: field.description,
          group: VENUE_GROUP,
          groupLabel: words.groupLabel(VENUE_GROUP, view),
          anchor: key,
          target,
          readOnly: false,
        });
      }
    }
    if (view === undefined) continue;
    for (const entry of view.settings) {
      if (target.scope.kind === 'venue' && storedOnlyAbove(entry) && organizationKeys.has(entry.key)) continue;
      const placement = placementOf(entry.key, entry.group);
      // A setting whose home is a panel on venue and organisation screens is revealed at that panel.
      const home =
        placement.home === 'pricing' && (target.scope.kind === 'venue' || target.scope.kind === 'organization')
          ? 'panel.payg'
          : placement.home === 'taxRegistration' && target.scope.kind === 'venue'
            ? 'panel.taxRegistration'
            : undefined;
      docs.push({
        id: `${target.id}:${entry.key}`,
        key: entry.key,
        label: words.label(entry.key),
        description: [words.help?.(entry.key) ?? entry.description, words.options?.(entry) ?? ''].filter(Boolean).join(' '),
        group: placement.group,
        groupLabel: words.groupLabel(placement.group, view),
        anchor: home ?? entry.key,
        target,
        readOnly: !entry.editable || entry.lockedAt !== undefined,
      });
    }
  }
  return docs;
}

function storedOnlyAbove(entry: SettingEntry): boolean {
  return !entry.scopes.some((scope) => VENUE_LEVEL.includes(scope));
}

// ---------------------------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------------------------

/**
 * Words folded to one before matching, longest phrase first: each row is the canonical word and what
 * reads as it. Applied to the query and the indexed text alike.
 */
const SYNONYMS: readonly (readonly [string, readonly string[]])[] = [
  ['payg', ['pay as you go', 'pay-as-you-go', 'payasyougo']],
  ['topup', ['top up', 'top-up', 'topups', 'top ups', 'recharges', 'recharge', 'reload']],
  ['upi', ['upi id', 'upi vpa', 'vpa', 'virtual payment address']],
  ['min', ['minimum', 'smallest', 'lowest', 'least']],
  ['max', ['maximum', 'largest', 'biggest', 'highest', 'most']],
  ['gst', ['tax']],
  ['pc', ['station', 'stations', 'pcs', 'computer', 'computers']],
  ['cutoff', ['cut off', 'cut-off', 'ends at', 'day end']],
  ['usb', ['pen drive', 'pendrive', 'flash drive', 'thumb drive']],
];

const PHRASES: readonly (readonly [RegExp, string])[] = SYNONYMS.flatMap(([canonical, variants]) =>
  [...variants]
    .sort((a, b) => b.length - a.length)
    .map((variant) => [new RegExp(`(^| )${variant.replace(/[-]/g, ' ').replace(/ /g, ' +')}(?= |$)`, 'g'), `$1${canonical}`] as const),
);

/** Lower-case words: camelCase and `a.b_c-d` split, punctuation dropped, synonyms folded. */
export function normalise(text: string): string {
  let out = ` ${text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()} `.replace(/ +/g, ' ');
  for (const [pattern, replacement] of PHRASES) out = out.replace(pattern, replacement);
  return out.trim();
}

const words = (text: string): string[] => (text === '' ? [] : text.split(' '));

/** The ranked settings for a query: at most `limit`, best first. A query under two letters lists none. */
export function rankSettings(docs: readonly SettingDoc[], query: string, limit = 8): SettingDoc[] {
  const needle = normalise(query);
  if (needle.replace(/ /g, '').length < 2) return [];
  const terms = words(needle);
  const scored: { doc: SettingDoc; score: number }[] = [];
  for (const doc of docs) {
    const score = scoreDoc(doc, needle, terms);
    if (score > 0) scored.push({ doc, score });
  }
  scored.sort((a, b) => b.score - a.score || a.doc.label.localeCompare(b.doc.label) || scopeRank(a.doc) - scopeRank(b.doc));
  return scored.slice(0, limit).map(({ doc }) => doc);
}

/** Within one setting, the venue first, then the organisation, then HQ's screens. */
function scopeRank(doc: SettingDoc): number {
  return ['venue', 'organization', 'hqOrganization', 'platform'].indexOf(doc.target.scope.kind);
}

interface Fields {
  readonly label: string[];
  readonly key: string[];
  readonly group: string[];
  readonly description: string[];
}

const fieldsCache = new WeakMap<SettingDoc, { fields: Fields; label: string }>();

function fieldsOf(doc: SettingDoc) {
  const cached = fieldsCache.get(doc);
  if (cached !== undefined) return cached;
  const label = normalise(doc.label);
  const made = {
    label,
    fields: {
      label: words(label),
      key: words(normalise(doc.key)),
      group: words(normalise(doc.groupLabel)),
      description: words(normalise(doc.description)),
    },
  };
  fieldsCache.set(doc, made);
  return made;
}

/** How well one word matches a field's words: whole word, then a word's start. */
function wordScore(field: readonly string[], term: string): number {
  if (field.includes(term)) return 3;
  if (field.some((word) => word.startsWith(term))) return 2;
  // A word typed inside a compound (`topupamount`): only for terms long enough not to be noise.
  if (term.length >= 4 && field.some((word) => word.includes(term))) return 1;
  return 0;
}

const WEIGHTS: Readonly<Record<keyof Fields, number>> = { label: 5, key: 4, group: 2, description: 1.5 };

function scoreDoc(doc: SettingDoc, needle: string, terms: readonly string[]): number {
  const { fields, label } = fieldsOf(doc);
  let total = 0;
  for (const term of terms) {
    let best = 0;
    for (const name of Object.keys(WEIGHTS) as (keyof Fields)[]) {
      best = Math.max(best, wordScore(fields[name], term) * WEIGHTS[name]);
    }
    // Every word must match somewhere: "grace" + "offline" is the offline grace, not every grace.
    if (best === 0) return 0;
    total += best;
  }
  // The label read as the query: the setting asked for by name.
  if (label === needle) total += 40;
  else if (label.startsWith(needle)) total += 20;
  else if (` ${label} `.includes(` ${needle}`)) total += 10;
  return total;
}
